import { Employee } from 'providers/employees/types'
import { immer } from 'zustand/middleware/immer'

type EmployeesTableStoreState = {
  section: string
  selected: Employee[]
  unselected: Employee[]
  isAllSelected: boolean
  individualAmounts: Record<Employee['idEmployee'], number>
}

type EmployeesTableStoreActions = {
  setIsAllSelected: (newState: boolean, section: string) => void
  resetAllData: () => void
  addInSelected: (employees: Employee[], section: string) => void
  removeFromSelected: (employees: Employee[], section: string) => void
  addInUnSelected: (employees: Employee[], section: string) => void
  removeFromUnSelected: (employees: Employee[], section: string) => void
  resetIndividualAmount: (employee: Employee) => void
  updateIndividualAmount: (employee: Employee, amount: number) => void
}

export type EmployeesTableStore = EmployeesTableStoreState &
  EmployeesTableStoreActions

export const initialState: EmployeesTableStoreState = {
  section: '',
  selected: [],
  unselected: [],
  isAllSelected: false,
  individualAmounts: {}
}

const employeeIsIncluded = (
  employees: Employee[],
  targetEmployee: Employee
) => {
  return employees.some(
    (employee) => employee.idEmployee === targetEmployee.idEmployee
  )
}

const getEmployeesNotIncluded = (
  currentEmployees: Employee[],
  newEmployees: Employee[]
) =>
  newEmployees.filter(
    (employee) => !employeeIsIncluded(currentEmployees, employee)
  )

export const employeesTableStoreCreator = immer<EmployeesTableStore>((set) => ({
  ...initialState,
  setIsAllSelected: (newState, section) => {
    set((state) => {
      state.isAllSelected = newState
      state.selected = []
      state.section = section
    })
  },
  addInSelected: (employees, section) => {
    set((state) => {
      const employeesNotIncludedInSelected = getEmployeesNotIncluded(
        state.selected,
        employees
      )
      state.section = section
      state.selected.push(...employeesNotIncludedInSelected)
      state.isAllSelected = false
      state.unselected = initialState.unselected
    })
  },
  removeFromSelected: (employees, section) => {
    set((state) => {
      const { selected } = state
      state.section = section
      state.isAllSelected = false
      state.selected = selected.filter(
        (selectedEmployee) => !employeeIsIncluded(employees, selectedEmployee)
      )
      state.unselected = initialState.unselected
    })
  },
  addInUnSelected: (employees, section) => {
    set((state) => {
      const employeesNotIncludedInUnSelected = getEmployeesNotIncluded(
        state.unselected,
        employees
      )
      state.section = section
      state.unselected.push(...employeesNotIncludedInUnSelected)
      state.isAllSelected = false
      state.selected = initialState.selected
    })
  },
  removeFromUnSelected: (employees, section) => {
    set((state) => {
      state.section = section
      state.isAllSelected = false
      state.unselected = state.unselected.filter(
        (unselectedEmployee) =>
          !employeeIsIncluded(employees, unselectedEmployee)
      )
      state.selected = initialState.selected
    })
  },
  updateIndividualAmount: (employee, amount) => {
    set((state) => {
      state.individualAmounts[employee.idEmployee] = amount
    })
  },
  resetIndividualAmount: (employee) => {
    set((state) => {
      const individualAmounts = state.individualAmounts
      delete individualAmounts[employee.idEmployee]

      state.individualAmounts = individualAmounts
    })
  },
  resetAllData: () => {
    set((state) => {
      state.section = initialState.section
      state.selected = initialState.selected
      state.isAllSelected = initialState.isAllSelected
      state.unselected = initialState.unselected
    })
  }
}))
